import { EMPTY, formatDateFull, formatDateNear, formatDecimal, formatInteger, formatPeriod, formatTime, isNumber, toInstant } from './piFormat.js';
import { SIZE_BAND_OPTIONS, optionLabel } from './piQuery.js';
import { companyRoute, featureRoute, viewPath } from './piNavigation.js';
import { classifyError, errorMessage } from './piEnvelope.js';
import { listOf, objectOf } from './piRequests.js';
import { defineColumns } from './piColumns.js';

export const OPPORTUNITY_STATUSES = [
  { value: 0, label: 'Nova', tone: 'info' },
  { value: 1, label: 'Relevante', tone: 'info' },
  { value: 2, label: 'Irrelevante', tone: 'muted' },
  { value: 3, label: 'Já conhecida', tone: 'muted' },
  { value: 4, label: 'Não elegível', tone: 'muted' },
  { value: 5, label: 'Contatada', tone: 'info' },
  { value: 6, label: 'Convertida', tone: 'ok' },
  { value: 7, label: 'Perdida', tone: 'muted' },
  { value: 8, label: 'Expirada', tone: 'muted' },
];

export const STATUS_TRANSITIONS = {
  0: [1, 2, 3, 4, 6],
  1: [5, 2, 4, 6],
  3: [5, 2, 6],
  5: [6, 7],
};

export const EXPIRING_STATUSES = [0, 1, 3];

export const CONVERTED_STATUS = 6;

export const STATUS_ACTIONS = {
  1: 'Marcar como relevante',
  2: 'Marcar como irrelevante',
  3: 'Marcar como já conhecida',
  4: 'Marcar como não elegível',
  5: 'Registrar contato',
  6: 'Marcar como convertida',
  7: 'Marcar como perdida',
};

export const STATUS_REASONS = {
  1: [
    { value: 'fit_confirmado', label: 'Fit confirmado' },
    { value: 'sinal_forte', label: 'Sinal forte' },
  ],
  2: [
    { value: 'sem_necessidade', label: 'Sem necessidade' },
    { value: 'sinal_errado', label: 'Sinal errado' },
    { value: 'porte_inadequado', label: 'Porte inadequado' },
  ],
  3: [
    { value: 'em_negociacao', label: 'Já em negociação' },
    { value: 'ja_ofertado', label: 'Já ofertado' },
  ],
  4: [
    { value: 'ja_tem_equivalente', label: 'Já tem equivalente' },
    { value: 'contrato_impede', label: 'Contrato impede' },
    { value: 'inadimplente', label: 'Inadimplente' },
    { value: 'cliente_em_risco', label: 'Cliente em risco' },
  ],
  5: [
    { value: 'email', label: 'E-mail' },
    { value: 'ligacao', label: 'Ligação' },
    { value: 'reuniao', label: 'Reunião' },
  ],
  6: [
    { value: 'fechou_upsell', label: 'Fechou upsell' },
    { value: 'fechou_teste', label: 'Fechou depois do teste' },
  ],
  7: [
    { value: 'preco', label: 'Preço' },
    { value: 'sem_prioridade', label: 'Sem prioridade' },
    { value: 'concorrente', label: 'Concorrente' },
    { value: 'sem_retorno', label: 'Sem retorno' },
  ],
};

export const SYSTEM_REASONS = [
  { value: 'nao_detectada', label: 'Não detectada de novo em 30 dias' },
  { value: 'substituida', label: 'Substituída por um sinal mais forte' },
  { value: 'empresa_demonstracao', label: 'Empresa marcada como demonstração' },
];

export const SYSTEM_ACTOR = 'system';

export const SYSTEM_ACTOR_TEXT = 'Sistema';

export const UNNAMED_ACTOR_TEXT = 'usuário do Master';

export const STRENGTHS = ['forte', 'moderada', 'fraca'];

export const COMMERCIAL_CLASSES = {
  avulso: 'Venda avulsa',
  base: 'Base do produto',
  incluida: 'Incluída no produto',
  sem_gate: 'Sem gate',
  parceiro: 'Parceiro',
  customizacao: 'Customização por cliente',
  prospect: 'Customização de prospect',
  tecnica: 'Técnica',
};

export const COMMERCIAL_STATES = [
  { key: 'em_teste', label: 'Em teste', short: 'Em teste' },
  { key: 'contratada', label: 'Contratada (há evidência comercial: preço ou venda inicial)', short: 'Contratada' },
  { key: 'padrao', label: 'Padrão (incluída para toda empresa)', short: 'Padrão' },
  { key: 'liberada_sem_evidencia', label: 'Liberada sem evidência comercial', short: 'Liberada sem evidência' },
  { key: 'equivalente_liberada', label: 'Equivalente liberada (V1/V2)', short: 'Equivalente liberada' },
  { key: 'sem_gate', label: 'Sem gate (todas as empresas têm)', short: 'Sem gate' },
  { key: 'nao_liberada', label: 'Não liberada', short: 'Não liberada' },
];

export const NO_RESPONSE_TEXT = 'Sem retorno: contatada há mais de 45 dias sem atualização';

export const OPEN_FILTER_LABEL = 'Abertas';

export const NO_RESPONSE_FILTER = 'no_response';

export const ALL_FILTER = 'all';

export const OPPORTUNITY_ORDER_TEXT = 'Ordem: abertas primeiro, depois pela força do sinal (forte, moderada, fraca) e pela detecção mais recente.';

export const RUN_STALE_HOURS = 36;

export const RULE_LEGEND = [
  { label: 'Ligada ou desligada', infoKey: 'revenue.rule.enabled' },
  { label: 'Aguardando dados', infoKey: 'revenue.rule.waiting' },
  { label: 'Cobertura parcial', infoKey: 'revenue.rule.partial' },
];

export const COMMERCIAL_LIMITATION = 'Estado comercial deduzido das liberações e vendas registradas, sem item de contrato por funcionalidade; sem valor estimado e sem probabilidade.';

export const OPPORTUNITY_FILTER_KEY = 'pi-opportunities';

export const OPPORTUNITY_FILTER_DEFAULTS = {
  status: '',
  rule: '',
  feature: '',
  size: '',
  company: '',
  companyName: '',
};

export const OPPORTUNITY_PAGE_SIZES = [10, 25, 50];

export const OPPORTUNITY_PAGE_SIZE = 10;

const RULE_KEY = /^[A-Za-z0-9_.:-]{1,40}$/;
const FEATURE = /^\d{1,4}$/;
const SIZE_BAND = /^[A-E]$/;
const COMPANY_ID = /^[0-9A-Fa-f-]{1,40}$/;
const STATUS = /^[0-8]$/;
const STATUS_FILTER = /^([0-8]|all|no_response)$/;
const WAITING_STATES = ['aguardando', 'insufficient'];
const QUIET_STATES = ['ok', 'parcial', 'desligada'];
const PARTIAL_STATE = 'parcial';

const textOf = (value) => (value === null || value === undefined ? '' : String(value).trim());

const numberOrNull = (value) => (isNumber(value) ? value : null);

const statusNumber = (value) => {
  if (isNumber(value)) return value;
  const raw = textOf(value);
  return STATUS.test(raw) ? Number(raw) : null;
};

const statusEntry = (status) => {
  const value = statusNumber(status);
  for (let i = 0; i < OPPORTUNITY_STATUSES.length; i++) {
    if (OPPORTUNITY_STATUSES[i].value === value) return OPPORTUNITY_STATUSES[i];
  }
  return null;
};

const fullDateTime = (value) => {
  const instant = toInstant(value);
  return instant ? formatDateFull(instant) + ' às ' + formatTime(instant) : EMPTY;
};

const dayText = (value) => {
  const text = formatDateFull(value);
  return text === EMPTY ? '' : text;
};

const catalogEntry = (catalog, status) => {
  const value = statusNumber(status);
  const source = objectOf(catalog);
  return value !== null && Object.prototype.hasOwnProperty.call(source, value) ? source[value] : null;
};

export const statusCatalog = (data) => {
  const catalog = {};
  listOf(objectOf(data).statuses).forEach((entry) => {
    const status = statusNumber(entry.status);
    if (status === null) return;
    catalog[status] = {
      label: textOf(entry.label),
      live: entry.live === true,
      next: (Array.isArray(entry.next) ? entry.next : []).map(statusNumber).filter((target) => target !== null),
      reasons: listOf(entry.reasons)
        .map((reason) => ({ value: textOf(reason.key), label: textOf(reason.label) }))
        .filter((reason) => reason.value),
    };
  });
  return Object.keys(catalog).length ? catalog : null;
};

export const statusLabel = (status, catalog) => {
  const known = catalogEntry(catalog, status);
  if (known && known.label) return known.label;
  const entry = statusEntry(status);
  return entry ? entry.label : EMPTY;
};

export const statusTone = (status) => {
  const entry = statusEntry(status);
  return entry ? entry.tone : 'muted';
};

export const statusOptions = (catalog) => OPPORTUNITY_STATUSES.map((entry) => ({ value: String(entry.value), label: statusLabel(entry.value, catalog) }));

export const statusFilterOptions = (catalog) => [{ value: NO_RESPONSE_FILTER, label: 'Sem retorno (contatada há mais de 45 dias)' }]
  .concat(statusOptions(catalog))
  .concat([{ value: ALL_FILTER, label: 'Todas' }]);

export const reasonOptions = (status, catalog) => {
  const value = statusNumber(status);
  if (value === null) return [];
  const known = catalogEntry(catalog, value);
  if (known && known.reasons.length) {
    return known.reasons.map((reason) => ({ value: reason.value, label: reason.label || reasonLabel(reason.value) }));
  }
  return (STATUS_REASONS[value] || []).slice();
};

export const validTransitions = (status, convertible, catalog) => {
  const value = statusNumber(status);
  if (value === null) return [];
  const known = catalogEntry(catalog, value);
  const targets = known ? known.next : (STATUS_TRANSITIONS[value] || []);
  return targets
    .filter((target) => !!STATUS_ACTIONS[target])
    .filter((target) => target !== CONVERTED_STATUS || EXPIRING_STATUSES.indexOf(value) === -1 || convertible === true)
    .map((target) => ({
      value: target,
      label: STATUS_ACTIONS[target],
      statusLabel: statusLabel(target, catalog),
      reasons: reasonOptions(target, catalog),
    }));
};

export const reasonLabel = (reason, catalog) => {
  const key = textOf(reason);
  if (!key) return '';
  const source = objectOf(catalog);
  const statuses = Object.keys(source);
  for (let i = 0; i < statuses.length; i++) {
    const found = listOf(source[statuses[i]].reasons).filter((option) => option.value === key && option.label)[0];
    if (found) return found.label;
  }
  const keys = Object.keys(STATUS_REASONS);
  for (let i = 0; i < keys.length; i++) {
    const found = STATUS_REASONS[keys[i]].filter((option) => option.value === key)[0];
    if (found) return found.label;
  }
  const system = SYSTEM_REASONS.filter((option) => option.value === key)[0];
  return system ? system.label : key;
};

export const actionAllows = (actions, to, reason) => listOf(actions)
  .some((action) => action.value === statusNumber(to) && listOf(action.reasons).some((option) => option.value === reason));

export const canChange = (from, to, reason, convertible, catalog) => actionAllows(validTransitions(from, convertible, catalog), to, reason);

export const actorText = (actor, name) => {
  const given = textOf(name);
  if (given) return given;
  const id = textOf(actor);
  if (!id || id.toLowerCase() === SYSTEM_ACTOR) return SYSTEM_ACTOR_TEXT;
  return UNNAMED_ACTOR_TEXT;
};

export const strengthText = (strength) => {
  const key = textOf(strength).toLowerCase();
  return 'Força do sinal: ' + (STRENGTHS.indexOf(key) !== -1 ? key : 'não informada');
};

export const commercialClassText = (value) => {
  const key = textOf(value).toLowerCase();
  if (!key) return '';
  return Object.prototype.hasOwnProperty.call(COMMERCIAL_CLASSES, key) ? COMMERCIAL_CLASSES[key] : textOf(value);
};

export const commercialStateLabel = (state) => {
  const key = textOf(state).toLowerCase();
  const found = COMMERCIAL_STATES.filter((entry) => entry.key === key)[0];
  return found ? found.label : (textOf(state) || 'Estado comercial não informado');
};

export const commercialStateShort = (state) => {
  const key = textOf(state).toLowerCase();
  const found = COMMERCIAL_STATES.filter((entry) => entry.key === key)[0];
  return found ? found.short : (textOf(state) || 'Estado comercial não informado');
};

export const commercialStateText = (commercialState) => {
  const source = objectOf(commercialState);
  const label = textOf(source.label) || commercialStateLabel(source.state);
  const verified = dayText(source.verifiedOn);
  return verified ? label + ' · conferido em ' + verified : label;
};

const signalValueText = (value) => {
  if (isNumber(value)) return Math.round(value) === value ? formatInteger(value) : formatDecimal(value, 1);
  if (value === true) return 'sim';
  if (value === false) return 'não';
  return textOf(value) || EMPTY;
};

const evidenceRow = (evidence, index) => {
  const source = objectOf(evidence);
  const rule = textOf(source.rule);
  const version = numberOrNull(source.version);
  const first = dayText(source.firstDetectedOn);
  const last = dayText(source.lastDetectedOn);
  const period = formatPeriod(source.periodFrom, source.periodTo);
  const coverage = dayText(source.coverageFrom);
  const parts = [];
  if (period !== EMPTY) parts.push('Período ' + period);
  if (coverage) parts.push('medição desde ' + coverage);
  if (first) parts.push('detectada em ' + first + (last && last !== first ? ', de novo em ' + last : ''));
  return {
    key: (rule || 'evidencia') + ':' + index,
    rule: rule,
    ruleText: (textOf(source.ruleName) || rule || 'Regra') + (version !== null ? ' (v' + version + ')' : ''),
    signals: listOf(source.signals).map((signal, signalIndex) => ({
      key: String(signalIndex),
      label: textOf(signal.label) || 'Sinal',
      value: signalValueText(signal.value),
    })),
    metaText: parts.join(' · '),
    coverageFrom: textOf(source.coverageFrom).substring(0, 10),
  };
};

export const convertedHintText = (hint) => {
  if (typeof hint === 'string') return hint.trim();
  const source = objectOf(hint);
  const released = dayText(source.releasedOn || source.on);
  if (!released) return '';
  const origin = textOf(source.originLabel) || textOf(source.origin);
  return 'Liberada em ' + released + (origin ? ' (origem: ' + origin + ')' : '') + '; quem marca convertida é Revenue.';
};

const earliest = (dates) => dates.filter(Boolean).sort()[0] || '';

const companyFacts = (company) => {
  const sizeBand = textOf(company.sizeBand);
  const seats = numberOrNull(company.seatsContracted);
  const parts = [];
  if (SIZE_BAND.test(sizeBand)) parts.push(optionLabel(SIZE_BAND_OPTIONS, sizeBand));
  else if (sizeBand) parts.push('Porte ' + sizeBand);
  if (textOf(company.planLabel)) parts.push('Plano ' + textOf(company.planLabel));
  if (seats !== null) parts.push(formatInteger(seats) + (seats === 1 ? ' assento contratado' : ' assentos contratados'));
  return parts.join(' · ');
};

const limitationList = (source, evidence) => {
  const coverage = earliest(evidence.map((row) => row.coverageFrom));
  const limitations = [];
  const addLimitation = (text) => {
    if (text && limitations.indexOf(text) === -1) limitations.push(text);
  };
  listOf(source.limitations).map(textOf).forEach(addLimitation);
  addLimitation(COMMERCIAL_LIMITATION);
  if (coverage) addLimitation('Sinais medidos desde ' + formatDateFull(coverage) + '.');
  return limitations;
};

const shardOf = (company) => textOf(company.shardCode) || textOf(company.shard);

export const opportunityView = (item, catalog) => {
  const source = objectOf(item);
  const company = objectOf(source.company);
  const feature = objectOf(source.feature);
  const status = statusNumber(source.status);
  const evidence = listOf(source.evidence).map(evidenceRow);
  const commercial = objectOf(source.commercialState);
  const limitations = limitationList(source, evidence);
  const statusBy = textOf(source.statusBy) || textOf(source.statusByName) ? actorText(source.statusBy, source.statusByName) : '';
  const statusAt = fullDateTime(source.statusAt);
  const statusParts = [];
  if (statusBy) statusParts.push('por ' + statusBy);
  if (statusAt !== EMPTY) statusParts.push('em ' + statusAt);
  const hint = convertedHintText(source.convertedHint);
  const companyName = textOf(company.name) || textOf(company.companyId) || 'Empresa';
  const featureName = textOf(feature.name) || (isNumber(feature.featureType) ? 'Funcionalidade ' + feature.featureType : 'Funcionalidade');
  const expires = dayText(source.expiresOn);
  const previousId = textOf(source.previousId);
  return {
    id: textOf(source.id),
    previousText: previousId ? 'Nova detecção depois de uma oportunidade encerrada (nº ' + previousId + ').' : '',
    title: companyName + ' · ' + featureName,
    company: {
      companyId: textOf(company.companyId),
      shard: shardOf(company),
      shardCode: shardOf(company),
      name: companyName,
      route: companyRoute(company.companyId, shardOf(company)),
      factsText: companyFacts(company),
    },
    feature: {
      featureType: isNumber(feature.featureType) ? feature.featureType : null,
      name: featureName,
      route: featureRoute(feature.featureType),
      classText: commercialClassText(feature.commercialClass),
    },
    commercialStateText: commercialStateText(commercial),
    strengthText: strengthText(source.strength),
    evidence: evidence,
    why: textOf(source.why),
    nextStep: textOf(source.nextStep),
    limitations: limitations,
    status: status,
    statusText: textOf(source.statusLabel) || statusLabel(status, catalog),
    statusTone: statusTone(status),
    reasonText: reasonLabel(source.reason, catalog),
    statusMetaText: statusParts.join(' '),
    detectedText: dayText(source.detectedOn) ? 'Detectada em ' + dayText(source.detectedOn) : '',
    expiresText: expires && !hint && EXPIRING_STATUSES.indexOf(status) !== -1 ? 'Expira em ' + expires + ' se não for detectada de novo' : '',
    noResponse: source.noResponse === true,
    convertedHintText: hint,
    actions: validTransitions(status, !!hint, catalog),
  };
};

export const eventRows = (events, catalog) => listOf(events)
  .map((event, index) => ({ event: event, index: index, at: toInstant(event.at) }))
  .sort((a, b) => {
    const left = a.at ? a.at.getTime() : 0;
    const right = b.at ? b.at.getTime() : 0;
    return left === right ? a.index - b.index : left - right;
  })
  .map((entry) => {
    const event = entry.event;
    const from = statusNumber(event.fromStatus);
    return {
      key: String(entry.index),
      atText: fullDateTime(event.at),
      byText: actorText(event.by, event.byName),
      changeText: from === null
        ? 'Criada como ' + statusLabel(event.toStatus, catalog)
        : statusLabel(from, catalog) + ' → ' + statusLabel(event.toStatus, catalog),
      reasonText: reasonLabel(event.reason, catalog),
    };
  });

export const ruleView = (rule, now) => {
  const source = objectOf(rule);
  const maturity = objectOf(source.maturity);
  const state = textOf(maturity.state).toLowerCase();
  const reason = textOf(maturity.reason);
  const enabled = source.enabled === true;
  const readyOn = formatDateNear(maturity.readyOn, now);
  const open = numberOrNull(source.open);
  const lastRun = fullDateTime(source.lastRunAt);
  const partial = enabled && state === PARTIAL_STATE;
  let maturityText = '';
  if (state && QUIET_STATES.indexOf(state) === -1) {
    if (WAITING_STATES.indexOf(state) !== -1 || readyOn !== EMPTY) {
      maturityText = readyOn !== EMPTY ? 'Aguardando dados até ' + readyOn : 'Aguardando dados';
    } else {
      maturityText = 'Sem dado';
    }
  }
  const meta = [];
  if (numberOrNull(source.version) !== null) meta.push('Versão ' + source.version);
  meta.push(lastRun !== EMPTY ? 'Última execução em ' + lastRun : 'Ainda não executada');
  meta.push(open === null ? 'Abertas: —' : formatInteger(open) + (open === 1 ? ' aberta' : ' abertas'));
  return {
    key: textOf(source.key),
    name: textOf(source.name) || textOf(source.key) || 'Regra',
    description: textOf(source.description),
    enabled: enabled,
    enabledText: enabled ? 'Ligada' : 'Desligada',
    waiting: enabled && !!maturityText,
    partial: partial,
    partialText: partial ? 'Cobertura parcial' : '',
    maturityText: maturityText,
    reasonText: reason ? 'Motivo: ' + reason : '',
    lastRunAt: textOf(source.lastRunAt) || null,
    lastRunText: lastRun !== EMPTY ? 'última execução em ' + lastRun : 'ainda não executada',
    metaText: meta.join(' · '),
  };
};

export const ruleRows = (rules, now) => listOf(rules).map((rule) => ruleView(rule, now));

export const ruleOptions = (rows, selected) => {
  const options = listOf(rows).filter((row) => row.key).map((row) => ({ value: row.key, label: row.name }));
  const wanted = textOf(selected);
  if (wanted && !options.some((option) => option.value === wanted)) options.push({ value: wanted, label: 'Regra ' + wanted });
  return options;
};

export const sanitizeOpportunityFilters = (filters) => {
  const source = objectOf(filters);
  const clean = Object.assign({}, OPPORTUNITY_FILTER_DEFAULTS);
  if (STATUS_FILTER.test(textOf(source.status))) clean.status = textOf(source.status);
  if (RULE_KEY.test(textOf(source.rule))) clean.rule = textOf(source.rule);
  if (FEATURE.test(textOf(source.feature))) clean.feature = String(Number(source.feature));
  if (SIZE_BAND.test(textOf(source.size))) clean.size = textOf(source.size);
  if (COMPANY_ID.test(textOf(source.company))) {
    clean.company = textOf(source.company);
    clean.companyName = textOf(source.companyName).replace(/\s+/g, ' ').substring(0, 120);
  }
  return clean;
};

export const opportunityFilterKey = (filters) => {
  const clean = sanitizeOpportunityFilters(filters);
  return [clean.status, clean.rule, clean.feature, clean.size, clean.company].join('|');
};

export const opportunityParams = (filters, page, pageSize) => {
  const clean = sanitizeOpportunityFilters(filters);
  const params = {};
  if (!clean.status) params.open = 'true';
  else if (clean.status === NO_RESPONSE_FILTER) params.noResponse = 'true';
  else if (clean.status !== ALL_FILTER) params.status = clean.status;
  if (clean.rule) params.rule = clean.rule;
  if (clean.feature) params.featureType = clean.feature;
  if (clean.size) params.sizeBand = clean.size;
  if (clean.company) params.companyId = clean.company;
  const pageNumber = Math.floor(Number(page));
  const sizeNumber = Math.floor(Number(pageSize));
  params.page = String(isFinite(pageNumber) && pageNumber >= 1 ? pageNumber : 1);
  params.pageSize = String(OPPORTUNITY_PAGE_SIZES.indexOf(sizeNumber) !== -1 ? sizeNumber : OPPORTUNITY_PAGE_SIZE);
  return params;
};

const optionText = (options, value) => {
  const found = listOf(options).filter((option) => String(option.value) === value)[0];
  return found && found.label ? found.label : value;
};

export const opportunityFilterLines = (filters, options) => {
  const clean = sanitizeOpportunityFilters(filters);
  const settings = objectOf(options);
  const lines = ['Status: ' + (clean.status ? optionText(settings.statuses, clean.status) : OPEN_FILTER_LABEL + ' (Nova, Relevante, Já conhecida e Contatada)')];
  if (clean.rule) lines.push('Regra: ' + optionText(settings.rules, clean.rule));
  if (clean.feature) lines.push('Funcionalidade: ' + optionText(settings.features, clean.feature));
  if (clean.size) lines.push('Porte: ' + optionLabel(SIZE_BAND_OPTIONS, clean.size));
  if (clean.company) lines.push('Empresa: ' + (clean.companyName || clean.company));
  lines.push(OPPORTUNITY_ORDER_TEXT);
  return lines;
};

export const opportunityFiltersActive = (filters) => {
  const clean = sanitizeOpportunityFilters(filters);
  return !!(clean.status || clean.rule || clean.feature || clean.size || clean.company);
};

export const opportunityLink = (companyId, companyName, featureType) => {
  const query = {};
  if (COMPANY_ID.test(textOf(companyId))) {
    query.company = textOf(companyId);
    if (textOf(companyName)) query.companyName = textOf(companyName).substring(0, 120);
  }
  if (FEATURE.test(textOf(featureType))) query.feature = String(Number(featureType));
  return { path: viewPath('opportunities'), query: query };
};

export const opportunitiesPayload = (data) => {
  const source = objectOf(data);
  const items = listOf(source.items);
  return {
    total: isNumber(source.total) && source.total >= 0 ? Math.floor(source.total) : items.length,
    items: items,
  };
};

export const opportunityCountText = (total, status) => {
  const count = isNumber(total) ? total : 0;
  if (!textOf(status)) return formatInteger(count) + (count === 1 ? ' oportunidade aberta' : ' oportunidades abertas');
  return formatInteger(count) + (count === 1 ? ' oportunidade' : ' oportunidades');
};

const ranRecently = (rule, reference) => {
  const at = toInstant(rule.lastRunAt);
  return !!at && reference.getTime() - at.getTime() <= RUN_STALE_HOURS * 3600 * 1000;
};

export const emptyOpportunitiesText = (rules, filtersActive, now) => {
  if (filtersActive) return 'Nenhuma oportunidade para os filtros escolhidos.';
  const enabled = listOf(rules).filter((rule) => rule.enabled);
  if (!listOf(rules).length) return 'Nenhuma oportunidade aberta.';
  if (!enabled.length) return 'Nenhuma oportunidade: todas as regras estão desligadas.';
  const waiting = enabled.filter((rule) => rule.waiting);
  if (waiting.length === enabled.length) {
    return 'Dados insuficientes: as regras ligadas ainda aguardam dados (' +
      waiting.map((rule) => rule.name + ': ' + rule.maturityText.toLowerCase()).join('; ') + ').';
  }
  const reference = toInstant(now) || new Date();
  const running = enabled.filter((rule) => !rule.waiting);
  const stale = running.filter((rule) => !ranRecently(rule, reference));
  if (stale.length) {
    return 'Lista vazia sem conclusão: ' + stale.map((rule) => rule.name + ' (' + rule.lastRunText + ')').join('; ') +
      '. Sem execução nas últimas ' + RUN_STALE_HOURS + ' horas, a lista vazia não significa ausência de sinal.';
  }
  const partial = running.filter((rule) => rule.partial);
  return 'Nenhuma oportunidade: as regras com dados rodaram e não encontraram sinal' +
    (partial.length ? ' (cobertura parcial: ' + partial.map((rule) => rule.name).join(', ') + '; veja o motivo em cada regra)' : '') + '.';
};

const REVENUE_DISABLED = 'Oportunidades está desligado neste ambiente ou ainda não foi publicado (404).';

export const revenueError = (error) => {
  if (!error) return error;
  if (error.kind === 'forbidden') {
    return Object.assign({}, error, { message: 'Sem acesso a Oportunidades (' + error.status + '): é preciso o acesso de Revenue, além do perfil de operador da plataforma.' });
  }
  if (error.kind === 'disabled') return Object.assign({}, error, { message: REVENUE_DISABLED });
  return error;
};

export const statusChangeError = (error) => {
  const info = classifyError(error);
  if (info.status === 409) return 'Esta mudança não vale mais para o status atual da oportunidade (409). Recarregue a lista para ver o status que está gravado.';
  if (info.status === 400) return info.message || 'Motivo fora da lista aceita para este status (400).';
  if (info.kind === 'forbidden') return 'Sem acesso de Revenue para mudar o status (' + info.status + ').';
  if (info.kind === 'disabled') return 'Oportunidade não encontrada ou Oportunidades desligado neste ambiente (404).';
  return errorMessage(error, 'mudar o status da oportunidade');
};

export const isConflict = (error) => classifyError(error).status === 409;

const companyOf = (item) => objectOf(objectOf(item).company);

const featureOf = (item) => objectOf(objectOf(item).feature);

const evidenceText = (item) => listOf(objectOf(item).evidence)
  .map(evidenceRow)
  .map((row) => {
    const signals = row.signals.map((signal) => signal.label + ': ' + signal.value).join('; ');
    return row.ruleText + (signals ? ': ' + signals : '') + (row.metaText ? ' (' + row.metaText + ')' : '');
  })
  .join(' | ');

const strengthKey = (strength) => {
  const key = textOf(strength).toLowerCase();
  return STRENGTHS.indexOf(key) !== -1 ? key : '';
};

const expiresOn = (item) => {
  const source = objectOf(item);
  const status = statusNumber(source.status);
  return EXPIRING_STATUSES.indexOf(status) !== -1 && !convertedHintText(source.convertedHint) ? textOf(source.expiresOn) : '';
};

const statusByText = (item) => {
  const source = objectOf(item);
  return textOf(source.statusBy) || textOf(source.statusByName) ? actorText(source.statusBy, source.statusByName) : '';
};

export const OPPORTUNITY_COLUMNS = defineColumns([
  {
    key: 'company', label: 'Empresa', type: 'text', infoKey: 'common.company', width: 36,
    value: (item) => textOf(companyOf(item).name) || textOf(companyOf(item).companyId),
  },
  { key: 'companyId', label: 'Identificador da empresa', type: 'text', infoKey: 'common.companyId', screen: false, value: (item) => textOf(companyOf(item).companyId) },
  { key: 'shardCode', label: 'Banco', type: 'text', infoKey: 'common.shard', screen: false, value: (item) => shardOf(companyOf(item)) },
  { key: 'sizeBand', label: 'Porte', type: 'text', infoKey: 'common.sizeBand', screen: false, value: (item) => textOf(companyOf(item).sizeBand) },
  { key: 'plan', label: 'Plano', type: 'text', infoKey: 'revenue.plan', screen: false, value: (item) => textOf(companyOf(item).planLabel) },
  {
    key: 'seatsContracted', label: 'Assentos contratados', type: 'int', infoKey: 'common.seatsContracted', screen: false,
    value: (item) => numberOrNull(companyOf(item).seatsContracted),
  },
  {
    key: 'feature', label: 'Funcionalidade', type: 'text', infoKey: 'revenue.feature', width: 30,
    value: (item) => textOf(featureOf(item).name) || (isNumber(featureOf(item).featureType) ? 'Funcionalidade ' + featureOf(item).featureType : ''),
  },
  {
    key: 'commercialClass', label: 'Classe comercial', type: 'text', infoKey: 'revenue.commercialClass', screen: false,
    value: (item) => commercialClassText(featureOf(item).commercialClass),
  },
  {
    key: 'status', label: 'Status', type: 'text', infoKey: 'revenue.status',
    value: (item) => textOf(objectOf(item).statusLabel) || statusLabel(objectOf(item).status, null),
  },
  {
    key: 'reason', label: 'Motivo do status', type: 'text', infoKey: 'revenue.reason', screen: false,
    value: (item) => textOf(objectOf(item).reasonLabel) || reasonLabel(objectOf(item).reason, null),
  },
  { key: 'statusBy', label: 'Status alterado por', type: 'text', infoKey: 'revenue.statusBy', screen: false, value: statusByText },
  { key: 'statusAt', label: 'Status alterado em', type: 'datetime', infoKey: 'revenue.statusAt', screen: false },
  { key: 'strength', label: 'Força do sinal', type: 'text', infoKey: 'revenue.strength', value: (item) => strengthKey(objectOf(item).strength) },
  { key: 'noResponse', label: 'Sem retorno', type: 'text', infoKey: 'revenue.noResponse', value: (item) => objectOf(item).noResponse === true },
  {
    key: 'commercialState', label: 'Estado comercial', type: 'text', infoKey: 'revenue.commercialState', width: 40,
    value: (item) => {
      const state = objectOf(objectOf(item).commercialState);
      return textOf(state.label) || commercialStateLabel(state.state);
    },
  },
  {
    key: 'verifiedOn', label: 'Estado comercial conferido em', type: 'date', infoKey: 'revenue.verifiedOn', screen: false,
    value: (item) => textOf(objectOf(objectOf(item).commercialState).verifiedOn),
  },
  { key: 'detectedOn', label: 'Detecção', type: 'date', infoKey: 'revenue.detectedOn' },
  { key: 'expiresOn', label: 'Expira em', type: 'date', infoKey: 'revenue.expiresOn', screen: false, value: expiresOn },
  { key: 'why', label: 'Observação', type: 'text', infoKey: 'revenue.why', width: 50 },
  { key: 'evidence', label: 'Evidência', type: 'text', infoKey: 'revenue.evidence', width: 60, value: evidenceText },
  {
    key: 'limitations', label: 'Limitações', type: 'text', infoKey: 'revenue.limitations', width: 60,
    value: (item) => limitationList(objectOf(item), listOf(objectOf(item).evidence).map(evidenceRow)).join(' | '),
  },
  { key: 'nextStep', label: 'Próximo passo sugerido', type: 'text', infoKey: 'revenue.nextStep', width: 50 },
  {
    key: 'id', label: 'Número da oportunidade', type: 'int', infoKey: 'revenue.opportunityId', screen: false,
    value: (item) => {
      const id = Number(textOf(objectOf(item).id));
      return textOf(objectOf(item).id) && isFinite(id) ? id : null;
    },
  },
]);



// WEBPACK FOOTER //
// ./src/vue/pages/components/pi/piOpportunitiesModel.js